import { Alert } from 'components/ui/alert';
import { LoadingMessage } from 'components/Loader/LoadingMessage';
import { RawDatum, RawDatumType } from 'interfaces/chart';
import { GeoFeatureDatum } from 'interfaces/geojson';
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery } from 'react-query';
import { getDataTypeMap } from 'utils/chart';
import { GEO_CODE } from 'utils/constants';
import { calculateIndicator } from 'utils/equation';
import { usePageCarto } from './usePageCarto';
import { useService } from './useService';
import { ContentType } from 'services/abstract';

type DatasetContextType = {
  rawData: RawDatum[];
  indicatorData: RawDatum[];
  dataKeys: Record<string, RawDatumType>;
  selectedGeoCode: string | null;
  selectGeoCode: (
    shapeDatum: GeoFeatureDatum | null,
    e: React.MouseEvent
  ) => void;
};

const initialContext: DatasetContextType = {
  rawData: [],
  indicatorData: [],
  dataKeys: {},
  selectedGeoCode: null,
  selectGeoCode: () => {
    /** noop **/
  },
};

const DatasetContext = createContext<DatasetContextType>(initialContext);

type DataProviderProps = {
  pageCartoId: number;
  geoCodeSelection: string | null;
  children: ReactNode;
};

export const DataProvider: FC<DataProviderProps> = ({
  pageCartoId,
  geoCodeSelection,
  children,
}) => {
  const [selectedGeoCode, setSelectedGeoCode] = useState<string | null>(
    geoCodeSelection
  );
  const selectedGeoFeatureRef = useRef<SVGElement | null>(null);
  const { indicators } = usePageCarto();
  const datasetService = useService(ContentType.DATASET);
  const {
    data: rawData,
    error,
    isLoading,
  } = useQuery({
    queryKey: ['page-carto-dataset', pageCartoId],
    queryFn: async () => await datasetService.getPageCartoData(pageCartoId),
    enabled: !!pageCartoId,
  });

  const indicatorData = useMemo(
    () =>
      rawData?.map((datum) => {
        // Calculate indicators
        return (indicators || []).reduce(
          (acc, indicator) => {
            acc[indicator.name] = calculateIndicator(indicator, datum);
            return acc;
          },
          { [GEO_CODE]: datum[GEO_CODE] } as RawDatum
        );
      }),
    [indicators, rawData]
  );

  const selectGeoCode = (
    shapeDatum: GeoFeatureDatum | null,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    // unselect current geo feature (if any)
    if (selectedGeoFeatureRef.current) {
      selectedGeoFeatureRef.current.classList.remove('selected-geo-feature');
    }
    // Select geo feature
    selectedGeoFeatureRef.current = e.target as SVGElement;
    if (selectedGeoFeatureRef.current.tagName === 'path') {
      selectedGeoFeatureRef.current.classList.add('selected-geo-feature');
    }
    const properties = shapeDatum?.feature?.properties;
    const geoCode =
      properties && GEO_CODE in properties ? properties[GEO_CODE] : null;
    setSelectedGeoCode(geoCode);
  };

  const dataKeys = useMemo(() => {
    if (selectedGeoCode && indicatorData && indicatorData.length > 0) {
      const datum =
        indicatorData.find((d) => d[GEO_CODE] === selectedGeoCode) ||
        indicatorData[0];
      if (datum) {
        return getDataTypeMap(datum);
      }
    }

    if (rawData && rawData.length > 0) {
      return getDataTypeMap(rawData[0]);
    }

    if (indicatorData && indicatorData.length > 0) {
      return getDataTypeMap(indicatorData[0]);
    }

    return {};
  }, [indicatorData, rawData, selectedGeoCode]);

  useEffect(() => {
    if (geoCodeSelection !== selectedGeoCode) {
      setSelectedGeoCode(geoCodeSelection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoCodeSelection]);

  if (isLoading) {
    return <LoadingMessage />;
  }

  if (error) {
    return (
      <Alert variant="destructive">Erreur lors du chargement des données</Alert>
    );
  }

  return (
    <DatasetContext.Provider
      value={{
        rawData: rawData || [],
        indicatorData: indicatorData || [],
        dataKeys,
        selectedGeoCode,
        selectGeoCode,
      }}
    >
      {children}
    </DatasetContext.Provider>
  );
};

export const useData = () => {
  return useContext(DatasetContext);
};
